import { authenticatedApiClient } from '../axios';
import {
  GroupComment,
  CommentInput,
  CommentsResponse,
  CreateCommentResponse,
  CommentType,
  CommentReaction,
  CommentError
} from '@cobu/types';
// import Logger from '@/services/Logger';

// API Response Types for the axios client
interface ApiCommentsResponse {
  comments: GroupComment[];
  hasMore: boolean;
  total: number;
  page: number;
}

interface ApiCreateCommentResponse {
  createdComment?: GroupComment;
  comment?: GroupComment;
  moderationType?: 'PROFANITY' | 'SENTIMENT';
  confidence?: number;
  flaggedTerms?: Array<{
    ruleId: string | number;
    match: string;
    start: number;
    end: number;
  }>;
}

interface ApiPinnedCommentsResponse extends Array<GroupComment> { }

interface ApiCommentReactionsResponse extends Array<CommentReaction> { }

/**
 * Comments Service
 * Handles all comment-related API operations using TanStack Query patterns
 */
class CommentsService {
  /**
   * Fetch comments for a group or group event
   */
  async getComments(
    groupId?: string,
    groupEventId?: string,
    page: number = 1,
    limit: number = 20,
    eventDate?: string
  ): Promise<CommentsResponse> {
    try {
      const params: any = {
        page,
        limit
      };

      let endpoint = '/v2/comments';

      if (groupId) {
        endpoint = `/groupcomment/group/${groupId}`;
      }

      const response = await authenticatedApiClient.get<ApiCommentsResponse>(endpoint, { params });

      return {
        comments: response.comments || [],
        hasMore: response.hasMore || false,
        total: response.total || 0,
        page: response.page || page
      };
    } catch (error: any) {
      throw this.handleError(error, 'Failed to fetch comments', 'getComments');
    }
  }

  /**
   * Fetch pinned comments for a group or building
   */
  async getPinnedComments(
    buildingId?: string,
    groupId?: string,
    limit: number = 7
  ): Promise<GroupComment[]> {
    try {
      const params: any = { limit };

      if (buildingId) {
        params.building = buildingId;
      }
      if (groupId) {
        params.group = groupId;
      }

      params.page = 1;
      const response = await authenticatedApiClient.get<ApiPinnedCommentsResponse>('/groupcomment/pinned', { params });
      return response || [];
    } catch (error: any) {
      throw this.handleError(error, 'Failed to fetch pinned comments', 'getPinnedComments');
    }
  }

  /**
   * Create a new comment or reply
   */
  async createComment(
    input: CommentInput,
    commentType: CommentType = CommentType.GroupComment
  ): Promise<CreateCommentResponse> {
    try {
      // Create FormData for multipart upload (to support images)
      const formData = new FormData();

      formData.append('contents', input.contents.trim());

      if (input.parent) {
        formData.append('parent', input.parent);
      }

      if (input.groupId) {
        formData.append('group', input.groupId);
      }

      if (input.groupEventId) {
        formData.append('groupEvent', input.groupEventId);
      }

      if (input.eventDate) {
        formData.append('interestedDate', input.eventDate);
      }

      if (input.confidence) {
        formData.append('confidence', input.confidence.toString());
      }

      if (input.tag_name) {
        formData.append('tag_name', input.tag_name);
      }

      if (input.wasHidden !== undefined) {
        formData.append('wasHidden', input.wasHidden.toString());
      }

      if (input.userDecision) {
        formData.append('userDecision', input.userDecision);
      }

      if (input.editingReport) {
        formData.append('editingReport', input.editingReport);
      }

      // Add images if provided
      if (input.images && input.images.length > 0) {
        input.images.forEach((image: any, index: number) => {
          if (image?.file instanceof File) {
            formData.append('images',
            image.file,
            image.file.name || `image_${index}.jpg`);
          } 
        });
      }

      let endpoint = '/v2/groupcomment';
      if (commentType === CommentType.GroupEventComment) {
        endpoint = '/v2/groupeventcomment';
      }

      const response = await authenticatedApiClient.post<ApiCreateCommentResponse>(endpoint, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return {
        comment: response.createdComment || response.comment,
        moderationType: response.moderationType,
        confidence: response.confidence,
        flaggedTerms: response.flaggedTerms,
      };
    } catch (error: any) {
      throw this.handleError(error, 'Failed to create comment', 'createComment');
    }
  }

  /**
   * Update an existing comment
   */
  async updateComment(
    commentId: string,
    updates: Partial<CommentInput>,
    commentType: CommentType = CommentType.GroupComment
  ): Promise<GroupComment> {
    try {
      const formData = new FormData();

      if (updates.contents) {
        formData.append('contents', updates.contents.trim());
      }

      // Add new images if provided
      if (updates.images && updates.images.length > 0) {
        updates.images.forEach((image, index) => {
          formData.append(`image_${index}`, image);
        });
      }

      let endpoint = `/v2/groupcomment/${commentId}`;
      if (commentType === CommentType.GroupEventComment) {
        endpoint = `/v2/groupeventcomment/${commentId}`;
      }

      const response = await authenticatedApiClient.put<GroupComment>(endpoint, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return response;
    } catch (error: any) {
      throw this.handleError(error, 'Failed to update comment', 'updateComment');
    }
  }

  /**
   * Delete a comment
   */
  async deleteComment(
    commentId: string,
    commentType: CommentType = CommentType.GroupComment
  ): Promise<void> {
    try {
      let endpoint = `/groupcomment/${commentId}`;
      if (commentType === CommentType.GroupEventComment) {
        endpoint = `/v2/groupeventcomment/${commentId}`;
      }

      await authenticatedApiClient.delete<void>(endpoint);
    } catch (error: any) {
      throw this.handleError(error, 'Failed to delete comment', 'deleteComment');
    }
  }

  /**
   * Add or remove a reaction to/from a comment
   */
  async reactToComment(
    commentId: string,
    reactionName: string | null,
    isRemoving: boolean = false,
    commentType: CommentType = CommentType.GroupComment,
    parentCommentId?: string,
    signal?: AbortSignal
  ): Promise<void> {
    try {
      const payload: any = {
        commentType,
        commentId,
      };

      if (parentCommentId) {
        payload.parent = parentCommentId;
      }

      if (isRemoving) {
        // Legacy parity: DELETE /reaction with body { commentType, commentId, parent }
        await authenticatedApiClient.delete<void>('/reaction', { data: payload, signal });
      } else {
        // Legacy parity: POST /reaction with body { commentType, commentId, parent, reactionName }
        payload.reactionName = reactionName!;
        await authenticatedApiClient.post<void>('/reaction', payload, { signal });
      }
    } catch (error: any) {
      throw this.handleError(error, 'Failed to react to comment', 'reactToComment');
    }
  }

  /**
   * Get reactions for a specific comment
   */
  async getCommentReactions(
    commentId: string,
    commentType: CommentType = CommentType.GroupComment
  ): Promise<CommentReaction[]> {
    try {
      let endpoint = `/groupcomment/${commentId}/reactions`;
      if (commentType === CommentType.GroupEventComment) {
        endpoint = `/groupeventcomment/${commentId}/reactions`;
      }

      const data = await authenticatedApiClient.get<ApiCommentReactionsResponse>(endpoint);
      return Array.isArray(data) ? data : [];
    } catch (error: any) {
      throw this.handleError(error, 'Failed to fetch comment reactions', 'getCommentReactions');
    }
  }

  /**
   * Report a comment or user
   */
  async reportComment(
    commentId: string,
    reason: string,
    additionalInfo?: string
  ): Promise<void> {
    try {
      const data = {
        commentId,
        reason,
        additionalInfo
      };

      await authenticatedApiClient.post<void>('/v2/reports/comment', data);
    } catch (error: any) {
      throw this.handleError(error, 'Failed to report comment', 'reportComment');
    }
  }

  /**
   * Pin or unpin a comment
   */
  async togglePinComment(
    commentId: string,
    isPinned: boolean,
    commentType: CommentType = CommentType.GroupComment
  ): Promise<void> {
    try {
      let endpoint = `/v2/groupcomment/${commentId}/pin`;
      if (commentType === CommentType.GroupEventComment) {
        endpoint = `/v2/groupeventcomment/${commentId}/pin`;
      }

      if (isPinned) {
        await authenticatedApiClient.post<void>(endpoint);
      } else {
        await authenticatedApiClient.delete<void>(endpoint);
      }
    } catch (error: any) {
      throw this.handleError(error, 'Failed to toggle comment pin status', 'togglePinComment');
    }
  }

  /**
   * Flag a comment for moderation
   */
  async flagComment(
    commentId: string,
    commentType: CommentType = CommentType.GroupComment
  ): Promise<void> {
    try {
      const endpoint = `/commentflagged`;
      const data = {
        comment: commentId,
        commentType,
      };
      await authenticatedApiClient.post<void>(endpoint, data);
    } catch (error: any) {
      throw this.handleError(error, 'Failed to flag comment', 'flagComment');
    }
  }

  /**
   * Get building-wide chat comments (public chat)
   */
  async getBuildingChatComments(
    buildingId: string,
    page: number = 1,
    limit: number = 7
  ): Promise<CommentsResponse> {
    try {
      const params = {
        building: buildingId,
        page,
        limit
      };
      const data = await authenticatedApiClient.get<GroupComment[]>('/globalcomment', { params });

      return {
        comments: data,
        hasMore: data.length === limit,
        page,
        total: -1
      };

    } catch (error: any) {
    //   Logger.log('getBuildingChatComments error', error);
      throw this.handleError(error, 'Failed to fetch building chat comments', 'getBuildingChatComments');
    }
  }

  /**
 * Get event chat comments (public chat)
 */
  async getEventChatComments(
    groupEventId: string,
    page: number = 1,
    limit: number = 10,
    eventDate?: string
  ): Promise<CommentsResponse> {
    try {
      const params: any = {};
      params.groupEvent = groupEventId;
      if (eventDate) {
        params.interestedDate = eventDate;
      }
      params.sort = "createdAt DESC";
      const data = await authenticatedApiClient.get<GroupComment[]>('/groupeventcomment', { params });

      return {
        comments: data,
        hasMore: false,
        page,
        total: -1
      };

    } catch (error: any) {
    //   Logger.log('getBuildingChatComments error', error);
      throw this.handleError(error, 'Failed to fetch building chat comments', 'getBuildingChatComments');
    }
  }


  /**
   * Search comments by content
   */
  async searchComments(
    query: string,
    groupId?: string,
    groupEventId?: string,
    page: number = 1,
    limit: number = 20
  ): Promise<CommentsResponse> {
    try {
      const params: any = {
        q: query,
        page,
        limit
      };

      if (groupId) {
        params.group = groupId;
      }
      if (groupEventId) {
        params.groupEvent = groupEventId;
      }

      const response = await authenticatedApiClient.get<ApiCommentsResponse>('/v2/comments/search', { params });

      return {
        comments: response.comments || [],
        hasMore: response.hasMore || false,
        total: response.total || 0,
        page: response.page || page
      };
    } catch (error: any) {
      throw this.handleError(error, 'Failed to search comments', 'searchComments');
    }
  }

  /**
   * Handle API errors and convert them to CommentError with improved specificity
   */
  private handleError(error: any, defaultMessage: string, context?: string): CommentError {
    console.error(`Comments service error${context ? ` in ${context}` : ''}:`, error.response);

    if (error.response) {
      const status = error.response.status;
      const message = error.response.data?.message || error.response.data?.error || defaultMessage;

      // Specific error handling for different status codes
      switch (status) {
        case 401:
          return {
            type: 'PERMISSION',
            message: 'You need to log in to perform this action.',
            details: error.response.data
          };
        case 403:
          return {
            type: 'PERMISSION',
            message: 'You don\'t have permission to perform this action.',
            details: error.response.data
          };
        case 404:
          return {
            type: 'VALIDATION',
            message: 'The requested comment or resource was not found.',
            details: error.response.data
          };
        case 422:
          return {
            type: 'VALIDATION',
            message: message,
            details: error.response.data
          };
        case 429:
          return {
            type: 'NETWORK',
            message: 'Too many requests. Please slow down and try again.',
            details: error.response.data
          };
        case 500:
        case 502:
        case 503:
        case 504:
          return {
            type: 'NETWORK',
            message: 'Server error. Please try again later.',
            details: error.response.data
          };
        default:
          return {
            type: 'NETWORK',
            message: message,
            details: error.response.data
          };
      }
    }

    if (error.request) {
      return {
        type: 'NETWORK',
        message: 'Network error. Please check your connection and try again.',
        details: error
      };
    }

    // Handle specific error types
    if (error.name === 'AbortError') {
      return {
        type: 'NETWORK',
        message: 'Request was cancelled.',
        details: error
      };
    }

    return {
      type: 'NETWORK',
      message: defaultMessage,
      details: error
    };
  }
}

// Export singleton instance
const commentsService = new CommentsService();
export default commentsService; 